/**
 * Labels as real DOM buttons (decision D7): large touch targets, keyboard-focusable, readable by screen readers.
 * Positioned by CSS2DRenderer; overlapping labels move to the left of their object or are hidden, greedily by
 * priority.
 */
import * as THREE from 'three';
import { CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';

export interface Label {
  id: string;
  object: CSS2DObject;
  button: HTMLButtonElement;
  /** Static importance; the focused object's label always wins. */
  basePriority: number;
  /** Effective priority this frame; higher wins when labels overlap. */
  priority: number;
  /** Set by the scene each frame: whether the label may be shown at all. */
  wanted: boolean;
  /** Offset from the anchor in px (e.g. the projected body radius). */
  offsetPx: number;
  /** Which side of its object the label sits on (decided by `declutter`). */
  side: 'right' | 'left';
  /**
   * No other label may cover this object's anchor, whatever its priority (planets: with a fleet, spacecraft labels
   * near a planet would otherwise catch the taps meant for it — S3-12).
   */
  protectAnchor: boolean;
}

/** Approximate width of one label character in px (spaced capitals, style B) — used by `declutter`. */
const LABEL_CHAR_PX = 9.5;

export function createLabel(
  id: string,
  text: string,
  color: string,
  onActivate: (id: string) => void,
  kind: 'body' | 'spacecraft' = 'body',
): Label {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'scene-label';
  button.dataset['id'] = id;
  button.dataset['kind'] = kind;
  button.style.setProperty('--label-color', color);
  const span = document.createElement('span');
  span.textContent = text;
  button.append(span);
  button.addEventListener('click', (e) => {
    e.stopPropagation();
    onActivate(id);
  });
  // Pointer events on labels must not start a camera drag.
  button.addEventListener('pointerdown', (e) => e.stopPropagation());
  const object = new CSS2DObject(button);
  object.center.set(0, 0.5);
  return {
    id,
    object,
    button,
    basePriority: 0,
    priority: 0,
    wanted: true,
    offsetPx: 0,
    side: 'right',
    protectAnchor: false,
  };
}

export function setLabelText(label: Label, text: string): void {
  const span = label.button.firstElementChild as HTMLSpanElement;
  if (span.textContent !== text) span.textContent = text;
}

const projected = new THREE.Vector3();

/** Half height of a label's tap area (44 px touch target) in px. */
const LABEL_HIT_HALF_PX = 22;
/** Radius around an object's anchor that another label must keep clear, in px. */
const ANCHOR_CLEAR_PX = 8;

interface Anchor {
  x: number;
  y: number;
  priority: number;
  label: Label;
}
const anchors: Anchor[] = [];

/**
 * Decides label visibility: hides labels behind the camera, labels inside the focused body's disc, labels that
 * overlap a higher-priority label (approximate boxes) and labels whose tap area covers the anchor of an object with
 * at least the same priority — otherwise a tap on that object would hit the label (Sprint 2: wider caps labels).
 */
export function declutter(
  labels: readonly Label[],
  camera: THREE.Camera,
  width: number,
  height: number,
  focusDisc: { x: number; y: number; r: number } | null,
): void {
  const boxes: { x0: number; x1: number; y0: number; y1: number }[] = [];
  const sorted = [...labels].sort((a, b) => b.priority - a.priority);
  for (let i = 0; i < sorted.length; i++) {
    const l = sorted[i]!;
    l.object.getWorldPosition(projected);
    projected.project(camera);
    const anchor = anchors[i] ?? (anchors[i] = { x: 0, y: 0, priority: 0, label: l });
    anchor.x = ((projected.x + 1) / 2) * width;
    anchor.y = ((1 - projected.y) / 2) * height;
    anchor.priority = l.wanted && projected.z <= 1 && projected.z >= -1 ? l.priority : -Infinity;
    anchor.label = l;
  }
  for (let i = 0; i < sorted.length; i++) {
    const l = sorted[i]!;
    let show = l.wanted;
    if (show) {
      const anchor = anchors[i]!;
      if (anchor.priority === -Infinity) show = false;
      const x = anchor.x + l.offsetPx;
      const y = anchor.y;
      if (show && focusDisc && l.priority < 100) {
        const dx = x - l.offsetPx - focusDisc.x;
        const dy = y - focusDisc.y;
        if (Math.hypot(dx, dy) < focusDisc.r) show = false;
      }
      if (show) {
        // right of the object first, then left of it; hidden if neither side is free
        const w = 14 + LABEL_CHAR_PX * (l.button.textContent?.length ?? 4);
        const left = anchor.x - l.offsetPx;
        const free = (x0: number): boolean => {
          const x1 = x0 + w;
          return (
            !boxes.some((b) => x0 < b.x1 && x1 > b.x0 && y - 10 < b.y1 && y + 10 > b.y0) &&
            !coversAnchor(l, anchor, x0, x1, sorted.length)
          );
        };
        const side = free(x) ? 'right' : free(left - w) ? 'left' : null;
        if (side) {
          const x0 = side === 'right' ? x : left - w;
          boxes.push({ x0, x1: x0 + w, y0: y - 10, y1: y + 10 });
          if (side !== l.side) {
            l.side = side;
            l.object.center.set(side === 'right' ? 0 : 1, 0.5);
            l.button.dataset['side'] = side;
          }
        } else show = false;
      }
    }
    l.object.visible = show;
    l.button.style.setProperty('--offset', `${Math.round(l.offsetPx)}px`);
  }
}

/**
 * Whether the tap area of label `l` (x0…x1, own anchor y ± hit half height) covers the anchor of another object with
 * at least the same priority. Objects at (almost) the same screen point as `l` itself (a spacecraft at its planet in
 * the system view) do not count: there the label cannot be moved off them anyway.
 */
function coversAnchor(l: Label, own: Anchor, x0: number, x1: number, n: number): boolean {
  const y = own.y;
  for (let i = 0; i < n; i++) {
    const a = anchors[i]!;
    if (a.label === l || a.priority === -Infinity || (a.priority < l.priority && !a.label.protectAnchor)) continue;
    if (Math.abs(a.x - own.x) < 2 * ANCHOR_CLEAR_PX && Math.abs(a.y - own.y) < 2 * ANCHOR_CLEAR_PX) continue;
    if (
      a.x + ANCHOR_CLEAR_PX > x0 &&
      a.x - ANCHOR_CLEAR_PX < x1 &&
      a.y + ANCHOR_CLEAR_PX > y - LABEL_HIT_HALF_PX &&
      a.y - ANCHOR_CLEAR_PX < y + LABEL_HIT_HALF_PX
    )
      return true;
  }
  return false;
}
